import type { ReactNode } from 'react';
import { cn } from './cn';

export type ScreenProps = {
  /** Shown as the page heading. Already translated by the caller. */
  title?: string;
  /** One line under the title. Optional, and usually better left out. */
  lede?: string;
  /** Top right slot: the language toggle, the sync badge, a back link. */
  actions?: ReactNode;
  /** Sticky bottom slot: the ops tab bar, or a single primary action. */
  footer?: ReactNode;
  /** Full bleed removes the reading-width clamp, for a photo panel. */
  bleed?: boolean;
  children: ReactNode;
  className?: string;
};

/**
 * The shell every screen sits in. It owns the safe areas, the reading width,
 * and the one place a skip link lives, so screens only write content.
 */
export function Screen({ title, lede, actions, footer, bleed = false, children, className }: ScreenProps) {
  return (
    <div className="min-h-dvh bg-ground text-ink font-body text-body flex flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:bg-surface focus:text-ink focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to content
      </a>

      {/*
        The header wraps rather than holding its line (defects D9 and D10).
        At 200 percent zoom a 390 pt phone is a 195 px viewport, and the actions
        alone (the sync badge plus the language toggle) are about 265 px, so a
        row that refused to wrap pushed both shells sideways by roughly 140 px
        and every screen scrolled horizontally. Section 9 asks for 200 percent
        zoom, and a person who needs it is the last person who should have to
        scroll sideways to read a heading.

        So: the row wraps, the title may shrink, the actions wrap inside
        themselves, and nothing is told not to shrink.
      */}
      {(title || actions) && (
        <header className="px-4 pt-[max(0.625rem,env(safe-area-inset-top))] pb-2 flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="min-w-0 flex-1 basis-[12rem]">
            {title && <h1 className="font-display text-[1.6em] leading-tight break-words">{title}</h1>}
            {lede && <p className="text-inkMuted text-caption mt-1">{lede}</p>}
          </div>
          {/* min-w-0 so the actions can be narrower than the widest thing in
              them. Without it a flex item refuses to go below its content and
              the header leaves the screen again. */}
          {actions && (
            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">{actions}</div>
          )}
        </header>
      )}

      <main id="main" className={cn('flex-1 px-4 pb-8', !bleed && 'max-w-reading w-full mx-auto', className)}>
        {children}
      </main>

      {footer && (
        <footer className="sticky bottom-0 bg-surface border-t border-line px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {footer}
        </footer>
      )}
    </div>
  );
}
